import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { FiUser, FiMail, FiPhone, FiMapPin, FiLock, FiEye, FiEyeOff, FiSave, FiEdit3, FiCheck, FiX, FiCamera, FiShield, FiCalendar, FiAward, FiUploadCloud, FiTrash2, FiUserPlus, FiActivity, FiAlertTriangle, FiCreditCard, FiFileText, FiExternalLink, FiStar } from 'react-icons/fi';
import { canAccessCobranzasEssential, canAccessProfesionalFeatures } from '../utils/profesionalAccess';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasMultimoneda } from '../utils/multimoneda';
import useFxRates from '../hooks/useFxRates';
import { useApi } from '../hooks/useApi';
import { FormAlert } from '../components/form';
import UserAvatar from '../components/UserAvatar';
import { isRemoteAvatar } from '../utils/avatar';
import FiscalSimulator from '../components/FiscalSimulator';
import FiscalChecklist from '../components/FiscalChecklist';
import { DEFAULT_WHATSAPP_TEMPLATES } from '../utils/whatsappTemplates';
import { CONTADOR_PACK_SECTION_OPTIONS, DEFAULT_CONTADOR_PACK_SECTIONS } from '../utils/contadorPackSections';
import ReferralPanel from '../components/ReferralPanel';
import CollectionPaymentSetupForm from '../components/CollectionPaymentSetupForm';
import { isCollectionPaymentConfigured } from '../utils/collectionPaymentSetup';
import { mustRegularizeSubscription } from '../utils/subscriptionGate';
import { getBetaProAccountInfo } from '../utils/subscriptionAccess';
import PlatformLedgerPanel from '../components/PlatformLedgerPanel';
import CuentaDesktopInstallPanel from '../components/CuentaDesktopInstallPanel';
import ComprobanteBrandingPanel from '../components/ComprobanteBrandingPanel';
import BetaProPageBanner from '../components/BetaProPageBanner';

const CUENTA_TABS = ['profile', 'security', 'subscription', 'referidos', 'fiscal', 'cobro', 'notifications', 'libro'];

const Cuenta = () => {
  const { user, refreshUser, updateUser } = useContext(AuthContext);
  const { secureFetch } = useApi();
  const { modoOscuro } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const requestedTab = new URLSearchParams(location.search).get('tab') || 'profile';
  const initialTab = requestedTab === 'info' ? 'profile' : requestedTab;
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState(
    CUENTA_TABS.includes(initialTab) ? initialTab : 'profile'
  );
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const avatarInputRef = useRef(null);
  const [fiscalForm, setFiscalForm] = useState({
    regimenFiscal: '',
    categoriaMonotributo: '',
    actividad: 'servicios',
    recordarCuotaMonotributo: true,
    vencimientoMonotributoDia: 20,
  });
  const [fiscalDisclaimer, setFiscalDisclaimer] = useState('');
  const [fiscalCalendario, setFiscalCalendario] = useState([]);
  const [fxPreference, setFxPreference] = useState('blue');
  const [fxSaving, setFxSaving] = useState(false);
  const multimoneda = hasMultimoneda(user);
  const hasProfesional = canAccessProfesionalFeatures(user);
  const hasCobranzasEssential = canAccessCobranzasEssential(user);
  const { preference: loadedFxPref, reload: reloadFx } = useFxRates();
  const [notificationForm, setNotificationForm] = useState({
    monthlyReportEnabled: false,
    monthlyReportDay: 1,
    weeklyReportEnabled: false,
    dailyDigestEnabled: false,
    collectionSequenceEnabled: false,
    paymentAlias: '',
    paymentCbu: '',
    paymentLink: '',
    paymentHolderName: '',
    paymentAccountConfirmed: false,
    promiseAlertsEnabled: true,
    contadorPackEnabled: false,
    contadorEmail: '',
    contadorPackDay: 5,
    contadorPackSchedule: null,
    contadorPackSections: { ...DEFAULT_CONTADOR_PACK_SECTIONS },
    collectionAlertsEnabled: false,
    collectionDsoThreshold: 45,
    collectionMinRate: 70,
    fiscalLimitAlertEnabled: false,
    whatsappTemplates: { ...DEFAULT_WHATSAPP_TEMPLATES },
  });
  const collectionPaymentConfigured = useMemo(
    () => isCollectionPaymentConfigured(notificationForm),
    [notificationForm]
  );

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    const normalized = tab === 'info' ? 'profile' : tab;
    if (normalized && CUENTA_TABS.includes(normalized)) {
      setActiveTab(normalized);
    }
  }, [location.search]);

  useEffect(() => {
    if (location.hash !== '#app-escritorio' || activeTab !== 'profile') return;
    const timer = window.setTimeout(() => {
      document.getElementById('app-escritorio')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.hash, activeTab]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await secureFetch('/api/platform-ledger/me');
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setIsLedgerAccount(Boolean(data.isLedgerAccount));
      } catch {
        if (!cancelled) setIsLedgerAccount(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, secureFetch]);

  const [notificationSaving, setNotificationSaving] = useState(false);
  const [collectionAlertTesting, setCollectionAlertTesting] = useState(false);
  const [fiscalAlertTesting, setFiscalAlertTesting] = useState(false);
  const [notificationTesting, setNotificationTesting] = useState(false);
  const [weeklyTesting, setWeeklyTesting] = useState(false);
  const [dailyDigestTesting, setDailyDigestTesting] = useState(false);
  const [contadorPackTesting, setContadorPackTesting] = useState(false);
  const [contadorInviteForm, setContadorInviteForm] = useState({ name: '', email: '' });
  const [contadorInvites, setContadorInvites] = useState([]);
  const [contadorInviteLoading, setContadorInviteLoading] = useState(false);
  const [contadorGuestLink, setContadorGuestLink] = useState('');
  const [contadorGuestLinkLoading, setContadorGuestLinkLoading] = useState(false);
  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [subscriptionCancelLoading, setSubscriptionCancelLoading] = useState(false);
  const [showCancelSubscription, setShowCancelSubscription] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, recoveryCodesRemaining: 0 });
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorRecoveryCodes, setTwoFactorRecoveryCodes] = useState([]);
  const [isLedgerAccount, setIsLedgerAccount] = useState(false);

  // Datos del formulario
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    company: user?.company || '',
    position: user?.position || ''
  });

  // Datos para cambio de contraseña
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        company: user.company || '',
        position: user.position || ''
      });
    }
  }, [user]);

  useEffect(() => {
    const loadFiscal = async () => {
      try {
        const res = await fetch('/api/fiscal/perfil', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setFiscalForm({
          regimenFiscal: data.perfil?.regimenFiscal || '',
          categoriaMonotributo: data.perfil?.categoriaMonotributo || '',
          actividad: data.perfil?.actividad || 'servicios',
          recordarCuotaMonotributo: data.perfil?.recordarCuotaMonotributo !== false,
          vencimientoMonotributoDia: data.perfil?.vencimientoMonotributoDia || 20,
        });
        setFiscalDisclaimer(data.parametria?.disclaimer || '');
      } catch {
        // silencioso
      }
    };
    const loadCalendario = async () => {
      try {
        const res = await fetch('/api/fiscal/calendario', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setFiscalCalendario(data.proximos || []);
      } catch {
        // silencioso
      }
    };
    loadFiscal();
    loadCalendario();
  }, []);

  useEffect(() => {
    if (loadedFxPref) setFxPreference(loadedFxPref);
  }, [loadedFxPref]);

  useEffect(() => {
    if (!hasCobranzasEssential) return;
    const loadNotifications = async () => {
      try {
        const res = await fetch('/api/notifications/preferences', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setNotificationForm({
          monthlyReportEnabled: Boolean(data.monthlyReportEnabled),
          monthlyReportDay: data.monthlyReportDay || 1,
          weeklyReportEnabled: Boolean(data.weeklyReportEnabled),
          dailyDigestEnabled: Boolean(data.dailyDigestEnabled),
          collectionSequenceEnabled: Boolean(data.collectionSequenceEnabled),
          paymentAlias: data.paymentAlias || '',
          paymentCbu: data.paymentCbu || '',
          paymentLink: data.paymentLink || '',
          paymentHolderName: data.paymentHolderName || '',
          paymentAccountConfirmed: Boolean(data.paymentAccountConfirmed),
          promiseAlertsEnabled: data.promiseAlertsEnabled !== false,
          contadorPackEnabled: Boolean(data.contadorPackEnabled),
          contadorEmail: data.contadorEmail || '',
          contadorPackDay: data.contadorPackDay || 5,
          contadorPackSchedule: data.contadorPackSchedule || null,
          contadorPackSections: {
            ...DEFAULT_CONTADOR_PACK_SECTIONS,
            ...(data.contadorPackSections || {}),
          },
          collectionAlertsEnabled: Boolean(data.collectionAlertsEnabled),
          collectionDsoThreshold: data.collectionDsoThreshold || 45,
          collectionMinRate: data.collectionMinRate || 70,
          fiscalLimitAlertEnabled: Boolean(data.fiscalLimitAlertEnabled),
          whatsappTemplates: {
            ...DEFAULT_WHATSAPP_TEMPLATES,
            ...(data.whatsappTemplates || {}),
          },
        });
      } catch {
        // silencioso
      }
    };
    loadNotifications();
  }, [hasCobranzasEssential]);

  useEffect(() => {
    if (activeTab !== 'subscription') return;
    let cancelled = false;

    const loadSubscription = async () => {
      setSubscriptionLoading(true);
      setSubscriptionError('');
      try {
        const response = await secureFetch('/api/payments/subscription/summary');
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'No se pudo cargar la suscripción');
        if (!cancelled) setSubscriptionSummary(data);
      } catch (error) {
        if (!cancelled) setSubscriptionError(error.message || 'No se pudo cargar la suscripción');
      } finally {
        if (!cancelled) setSubscriptionLoading(false);
      }
    };

    loadSubscription();
    return () => {
      cancelled = true;
    };
  }, [activeTab, secureFetch]);

  const betaAccountInfo = useMemo(() => {
    if (!user || !subscriptionSummary?.subscription) return null;
    return getBetaProAccountInfo({
      ...user,
      subscription: subscriptionSummary.subscription,
    });
  }, [user, subscriptionSummary]);

  useEffect(() => {
    if (activeTab !== 'security') return;
    let cancelled = false;
    const loadTwoFactorStatus = async () => {
      try {
        const response = await secureFetch('/api/auth/2fa/status');
        const data = await response.json();
        if (!response.ok) throw new Error(data.message);
        if (!cancelled) setTwoFactorStatus(data);
      } catch (error) {
        if (!cancelled) setMessage({ type: 'error', text: error.message || 'No se pudo consultar 2FA.' });
      }
    };
    loadTwoFactorStatus();
    return () => {
      cancelled = true;
    };
  }, [activeTab, secureFetch]);

  useEffect(() => {
    if (!hasProfesional) return;
    const loadContadorInvites = async () => {
      try {
        const res = await secureFetch('/api/contador/invites');
        if (!res.ok) return;
        const data = await res.json();
        setContadorInvites(Array.isArray(data.invites) ? data.invites : []);
      } catch {
        // silencioso
      }
    };
    loadContadorInvites();
  }, [hasProfesional, secureFetch]);

  const handleContadorInvite = async () => {
    if (!contadorInviteForm.name.trim() || !contadorInviteForm.email.trim()) {
      setMessage({ type: 'error', text: 'Nombre y email del contador son obligatorios.' });
      return;
    }
    setContadorInviteLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await secureFetch('/api/contador/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contadorInviteForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al invitar');
      setMessage({ type: 'success', text: data.message });
      setContadorInviteForm({ name: '', email: '' });
      if (!data.linked) {
        const listRes = await secureFetch('/api/contador/invites');
        const listData = await listRes.json();
        setContadorInvites(Array.isArray(listData.invites) ? listData.invites : []);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setContadorInviteLoading(false);
    }
  };

  const handleContadorGuestLink = async (rotate = false) => {
    setContadorGuestLinkLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await secureFetch('/api/contador/guest-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rotate }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo generar el link');
      setContadorGuestLink(data.url || '');
      setMessage({ type: 'success', text: data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setContadorGuestLinkLoading(false);
    }
  };

  const copyContadorGuestLink = async () => {
    if (!contadorGuestLink) return;
    try {
      await navigator.clipboard.writeText(contadorGuestLink);
      setMessage({ type: 'success', text: 'Link copiado al portapapeles.' });
    } catch {
      setMessage({ type: 'error', text: 'No pudimos copiar el link. Copialo manualmente.' });
    }
  };

  const handleNotificationSave = async (e) => {
    e.preventDefault();
    setNotificationSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch('/api/notifications/preferences', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(notificationForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar');
      setNotificationForm((prev) => ({
        ...prev,
        paymentHolderName: data.paymentHolderName ?? prev.paymentHolderName,
        paymentAccountConfirmed: Boolean(data.paymentAccountConfirmed),
      }));
      setMessage({ type: 'success', text: 'Preferencias de notificaciones guardadas.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setNotificationSaving(false);
    }
  };

  const handleTestWeeklyReport = async () => {
    setWeeklyTesting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch('/api/notifications/weekly-report/test', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al enviar prueba');
      setMessage({ type: 'success', text: data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setWeeklyTesting(false);
    }
  };

  const handleTestDailyDigest = async () => {
    setDailyDigestTesting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch('/api/notifications/daily-digest/test', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al enviar prueba');
      setMessage({ type: 'success', text: data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setDailyDigestTesting(false);
    }
  };

  const handleTestCollectionAlert = async () => {
    setCollectionAlertTesting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await secureFetch('/api/notifications/collection-alert/test', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al enviar prueba');
      setMessage({ type: 'success', text: data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCollectionAlertTesting(false);
    }
  };

  const handleTestFiscalAlert = async () => {
    setFiscalAlertTesting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await secureFetch('/api/notifications/fiscal-limit-alert/test', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al enviar prueba');
      setMessage({ type: 'success', text: data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setFiscalAlertTesting(false);
    }
  };

  const handleTestContadorPack = async () => {
    setContadorPackTesting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch('/api/notifications/contador-pack/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ contadorEmail: notificationForm.contadorEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al enviar prueba');
      setMessage({ type: 'success', text: data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setContadorPackTesting(false);
    }
  };

  const handleTestMonthlyReport = async () => {
    setNotificationTesting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch('/api/notifications/monthly-report/test', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al enviar prueba');
      setMessage({ type: 'success', text: data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setNotificationTesting(false);
    }
  };

  const handleFxPreferenceSave = async (pref) => {
    setFxSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch('/api/fx/preference', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ preference: pref }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar preferencia');
      setFxPreference(pref);
      reloadFx();
      setMessage({ type: 'success', text: 'Cotización actualizada. Se usará en nuevos registros en USD/EUR.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setFxSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al actualizar el perfil');
      }

      if (data.user) updateUser(data.user);
      await refreshUser();
      setMessage({ type: 'success', text: 'Perfil actualizado correctamente' });
      setEditMode(false);
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleFiscalSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await fetch('/api/fiscal/perfil', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          regimenFiscal: fiscalForm.regimenFiscal || null,
          categoriaMonotributo: fiscalForm.regimenFiscal === 'monotributo' ? fiscalForm.categoriaMonotributo : null,
          actividad: fiscalForm.actividad,
          recordarCuotaMonotributo: fiscalForm.recordarCuotaMonotributo,
          vencimientoMonotributoDia: fiscalForm.vencimientoMonotributoDia,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar el perfil fiscal');
      const successText = data.recurringMessage
        ? `Perfil fiscal actualizado. ${data.recurringMessage}`
        : 'Perfil fiscal actualizado';
      setMessage({ type: 'success', text: successText });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres' });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/users/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al cambiar la contraseña');
      }

      setMessage({ type: 'success', text: 'Contraseña actualizada correctamente' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const startTwoFactorSetup = async () => {
    setTwoFactorLoading(true);
    setMessage({ type: '', text: '' });
    setTwoFactorRecoveryCodes([]);
    try {
      const response = await secureFetch('/api/auth/2fa/setup', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo iniciar la configuración');
      setTwoFactorSetup(data);
      setTwoFactorCode('');
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const enableTwoFactor = async (event) => {
    event.preventDefault();
    setTwoFactorLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await secureFetch('/api/auth/2fa/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setupToken: twoFactorSetup?.setupToken,
          code: twoFactorCode,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo activar 2FA');
      setTwoFactorStatus({ enabled: true, recoveryCodesRemaining: data.recoveryCodes?.length || 0 });
      setTwoFactorRecoveryCodes(data.recoveryCodes || []);
      setTwoFactorSetup(null);
      setTwoFactorCode('');
      setMessage({ type: 'success', text: data.message });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const disableTwoFactor = async (event) => {
    event.preventDefault();
    setTwoFactorLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await secureFetch('/api/auth/2fa/disable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: twoFactorCode }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo desactivar 2FA');
      setTwoFactorStatus({ enabled: false, recoveryCodesRemaining: 0 });
      setTwoFactorRecoveryCodes([]);
      setTwoFactorCode('');
      setMessage({ type: 'success', text: data.message });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const cancelEdit = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: user?.address || '',
      company: user?.company || '',
      position: user?.position || ''
    });
    setEditMode(false);
    setMessage({ type: '', text: '' });
  };

  const validateAvatarFile = (file) => {
    if (!file) return 'Elegí una imagen.';
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) return 'Solo se aceptan JPG, PNG, WEBP o GIF.';
    if (file.size > 5 * 1024 * 1024) return 'La imagen debe pesar menos de 5 MB.';
    return null;
  };

  const uploadAvatarFile = async (file) => {
    const invalid = validateAvatarFile(file);
    if (invalid) {
      setMessage({ type: 'error', text: invalid });
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);

    setAvatarLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const body = new FormData();
      body.append('avatar', file);
      const response = await secureFetch('/api/users/avatar', { method: 'POST', body });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al subir el avatar');

      updateUser({ avatar: data.avatar || data.user?.avatar });
      setAvatarVersion(Date.now());
      setPreviewUrl(null);
      await refreshUser();
      setMessage({ type: 'success', text: 'Foto de perfil actualizada.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setPreviewUrl(null);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadAvatarFile(file);
  };

  const handleAvatarDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadAvatarFile(file);
  };

  const removeAvatar = async () => {
    setAvatarLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await secureFetch('/api/users/avatar', { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al eliminar el avatar');
      updateUser({ avatar: null });
      setAvatarVersion(Date.now());
      setPreviewUrl(null);
      await refreshUser();
      setMessage({ type: 'success', text: 'Foto de perfil eliminada.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setAvatarLoading(false);
    }
  };

  if (!user) {
    return (
      <div className={modoOscuro ? 'flex items-center justify-center min-h-screen bg-black text-white' : 'flex items-center justify-center min-h-screen bg-[#f5f5f7] text-gray-900'}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-10 w-10 border-2 mx-auto mb-3 ${modoOscuro ? 'border-gray-600 border-t-gray-300' : 'border-gray-300 border-t-gray-900'}`}></div>
          <p className={modoOscuro ? 'text-sm text-gray-300' : 'text-sm text-gray-700'}>Cargando tu cuenta...</p>
        </div>
      </div>
    );
  }

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const tabInactive = modoOscuro ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50';
  const avatarHint = isRemoteAvatar(user?.avatar)
    ? 'Foto de Google. Podés reemplazarla con una propia.'
    : user?.avatar
      ? 'Foto personalizada. Arrastrá otra imagen para cambiarla.'
      : 'Arrastrá una imagen o hacé clic en la foto.';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const inputClass = modoOscuro ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-gray-500 focus:border-gray-500' : 'border-gray-300 bg-white focus:ring-gray-900 focus:border-gray-900';
  const inputDisabled = modoOscuro ? 'border-gray-700 bg-gray-800/50 text-gray-500' : 'border-gray-200 bg-gray-50 text-gray-500';
  const btnOut = modoOscuro ? 'border-gray-600 text-white hover:bg-gray-800' : 'border-gray-300 text-gray-800 hover:bg-gray-50';

  const formatAccountDate = (value, withTime = false) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    });
  };
  const formatBillingAmount = (amount, currency = 'ARS') =>
    new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  const subscriptionStatus = (() => {
    const sub = subscriptionSummary?.subscription || {};
    if (sub.cancelAtPeriodEnd) return 'Sin renovación';
    const key = String(sub.status || '').toLowerCase();
    return {
      active: 'Activa',
      trialing: 'Período de prueba',
      authorized: 'Activa',
      pending_payment: 'Pago pendiente',
      past_due: 'Pago vencido',
      grace_readonly: 'Gracia (solo lectura)',
      suspended: 'Suspendida',
      purged: 'Cuenta dada de baja',
      canceled: 'Cancelada',
      cancelled: 'Cancelada',
      none: 'Sin suscripción',
    }[key] || 'Sin información';
  })();

  const canCancelSubscription = Boolean(subscriptionSummary?.policy?.canCancel);

  const subscriptionPolicyBullets = [
    'No hay reembolso ni crédito por el tiempo restante del período ya pagado.',
    'Seguís usando el plan hasta la fecha de renovación o fin de prueba que figura arriba.',
    'En período de prueba, cancelar solo detiene el cobro futuro; el acceso dura hasta el fin de la prueba.',
    'No ofrecemos pausa gratuita: solo renovación activa o baja al vencer el período.',
    'Para volver después, elegís un plan de nuevo (nuevo cobro en Mercado Pago).',
  ];

  const handleCancelSubscription = async () => {
    setSubscriptionCancelLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await secureFetch('/api/payments/subscription/cancel', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo cancelar la suscripción');
      setShowCancelSubscription(false);
      setMessage({ type: 'success', text: data.message || 'Suscripción cancelada al fin del período.' });
      const summaryRes = await secureFetch('/api/payments/subscription/summary');
      const summary = await summaryRes.json();
      if (summaryRes.ok) setSubscriptionSummary(summary);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'No se pudo cancelar la suscripción' });
    } finally {
      setSubscriptionCancelLoading(false);
    }
  };
  const paymentStatus = (status) => ({
    active: 'Activo',
    authorized: 'Aprobado',
    approved: 'Aprobado',
    trialing: 'Período de prueba',
    pending: 'Pendiente',
    pending_payment: 'Pendiente',
    rejected: 'Rechazado',
    canceled: 'Cancelado',
    cancelled: 'Cancelado',
  }[String(status || '').toLowerCase()] || status || '—');

  const tabs = [
    { id: 'profile', label: 'Perfil', short: 'Perfil' },
    { id: 'security', label: 'Seguridad', short: 'Seg.' },
    { id: 'subscription', label: 'Suscripción', short: 'Plan' },
    { id: 'fiscal', label: 'Fiscal', short: 'Fiscal' },
    ...(hasCobranzasEssential ? [
      { id: 'cobro', label: 'Cobrar', short: 'Cobrar', pending: !collectionPaymentConfigured },
    ] : []),
    ...(hasProfesional ? [
      { id: 'notifications', label: 'Notificaciones', short: 'Avisos' },
    ] : []),
    { id: 'referidos', label: 'Referidos', short: 'Ref.' },
    ...(isLedgerAccount ? [{ id: 'libro', label: 'Libro Contsuite', short: 'Libro' }] : []),
  ];

  return (
    <div className={modoOscuro ? 'min-h-[calc(100dvh-4rem)] bg-black text-white' : 'min-h-[calc(100dvh-4rem)] bg-[#f5f5f7] text-gray-900'}>
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-3 pb-8 space-y-4">
        <header className="flex items-center gap-3 min-w-0">
          <UserAvatar user={user} size="lg" version={avatarVersion} previewUrl={previewUrl} rounded="2xl" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className={`text-xl sm:text-2xl font-semibold leading-tight ${textPri}`}>Mi cuenta</h1>
              <BetaProPageBanner user={user} />
            </div>
            <p className={`text-xs truncate mt-0.5 ${textSec}`}>{user.name} · {user.email}</p>
          </div>
        </header>

        <FormAlert type={message.type === 'success' ? 'success' : 'error'}>{message.text}</FormAlert>

        <div className={`rounded-xl border p-0.5 ${box}`}>
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setActiveTab(t.id);
                  navigate(`/cuenta?tab=${t.id}`, { replace: true });
                }}
                className={`relative flex-1 min-w-0 px-2 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-sm font-medium ${
                  activeTab === t.id ? 'bg-[#1F80FF] text-white' : tabInactive
                }`}
              >
                <span className="sm:hidden">{t.short}</span>
                <span className="hidden sm:inline">{t.label}</span>
                {t.pending && activeTab !== t.id && (
                  <span
                    className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500"
                    aria-hidden
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {hasCobranzasEssential && !collectionPaymentConfigured && activeTab !== 'cobro' && (
          <div
            className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border px-3 py-2.5 text-xs ${
              modoOscuro ? 'border-amber-800/50 bg-amber-950/20 text-amber-100' : 'border-amber-200 bg-amber-50/90 text-amber-950'
            }`}
          >
            <span className={modoOscuro ? 'text-amber-200/90' : 'text-amber-900'}>
              <span className="font-semibold">Medios de cobro pendientes.</span>
              {' '}Alias, CBU o link para Cobrar y WhatsApp.
            </span>
            <button
              type="button"
              onClick={() => navigate('/cuenta?tab=cobro')}
              className="shrink-0 font-semibold text-[#1F80FF] hover:underline text-left sm:text-right"
            >
              Completar en Cobrar →
            </button>
          </div>
        )}

        {/* Contenido de las tabs */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 w-full">
          {activeTab === 'profile' && (
            <>
              <div className={`${box} rounded-2xl border overflow-hidden`}>
                <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${modoOscuro ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                      <FiCamera className={`text-base ${modoOscuro ? 'text-gray-300' : 'text-gray-900'}`} />
                    </div>
                    <div className="min-w-0">
                      <h2 className={`text-sm font-semibold ${textPri}`}>Foto de perfil</h2>
                      <p className={`text-xs ${textMuted}`}>Se ve en el menú y en tu cuenta.</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => !avatarLoading && avatarInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        avatarInputRef.current?.click();
                      }
                    }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleAvatarDrop}
                    className={`relative rounded-2xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
                      dragOver
                        ? 'border-[#1F80FF] bg-[#1F80FF]/10'
                        : modoOscuro
                          ? 'border-gray-700 bg-gray-800/40 hover:border-gray-500'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    } ${avatarLoading ? 'pointer-events-none opacity-80' : 'cursor-pointer'}`}
                  >
                    <div className="relative inline-block">
                      <UserAvatar
                        user={user}
                        size="xl"
                        version={avatarVersion}
                        previewUrl={previewUrl}
                      />
                      <span className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#1F80FF] text-white flex items-center justify-center shadow-md">
                        {avatarLoading ? (
                          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        ) : (
                          <FiCamera className="w-4 h-4" />
                        )}
                      </span>
                    </div>
                    <p className={`mt-4 text-sm font-medium ${textPri}`}>
                      {avatarLoading ? 'Subiendo foto…' : dragOver ? 'Soltá la imagen' : 'Clic o arrastrá una foto'}
                    </p>
                    <p className={`mt-1 text-xs ${textMuted}`}>{avatarHint}</p>
                    <p className={`mt-2 text-[11px] ${textMuted}`}>JPG, PNG, WEBP o GIF · máx. 5 MB</p>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={avatarLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1F80FF] text-white disabled:opacity-50"
                    >
                      <FiUploadCloud className="w-3.5 h-3.5" />
                      {user?.avatar ? 'Cambiar foto' : 'Subir foto'}
                    </button>
                    {user?.avatar && (
                      <button
                        type="button"
                        onClick={removeAvatar}
                        disabled={avatarLoading}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-50 ${btnOut}`}
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                        Quitar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Información del perfil */}
              <div className={`${box} rounded-2xl border overflow-hidden`}>
                <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 ${modoOscuro ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                        <FiUser className={`text-base ${modoOscuro ? 'text-gray-300' : 'text-gray-900'}`} />
                      </div>
                      <div className="min-w-0">
                        <h2 className={`text-sm font-semibold ${textPri}`}>Información personal</h2>
                        <p className={`text-xs ${textMuted}`}>Datos básicos de tu perfil.</p>
                      </div>
                    </div>
                    {!editMode ? (
                      <button
                        onClick={() => setEditMode(true)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${modoOscuro ? 'border-gray-400 text-white hover:bg-gray-700' : 'border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white'}`}
                      >
                        <FiEdit3 className="text-sm" />
                        <span>Editar</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={cancelEdit}
                          className={`flex items-center justify-center gap-1.5 px-3 py-1.5 border text-xs font-medium rounded-lg transition-colors ${btnOut}`}
                        >
                          <FiX className="text-sm" />
                          <span>Cancelar</span>
                        </button>
                        <button
                          onClick={handleProfileUpdate}
                          disabled={loading}
                          className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${modoOscuro ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-900 hover:bg-black'}`}
                        >
                          {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <FiSave className="text-sm" />
                          )}
                          <span className="hidden sm:inline">Guardar</span>
                          <span className="sm:hidden">Guardar</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <form onSubmit={handleProfileUpdate} className="p-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className={`block text-xs font-medium ${textSec}`}>
                        Nombre completo
                      </label>
                      <div className="relative">
                        <FiUser className={`absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${textMuted}`} />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-1 transition-colors ${editMode ? inputClass : inputDisabled}`}
                          placeholder="Tu nombre completo"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className={`block text-xs font-medium ${textSec}`}>
                        Email
                      </label>
                      <div className="relative">
                        <FiMail className={`absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${textMuted}`} />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-1 transition-colors ${editMode ? inputClass : inputDisabled}`}
                          placeholder="tu@email.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className={`block text-xs font-medium ${textSec}`}>
                        Teléfono
                      </label>
                      <div className="relative">
                        <FiPhone className={`absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${textMuted}`} />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-1 transition-colors ${editMode ? inputClass : inputDisabled}`}
                          placeholder="+54 11 1234-5678"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className={`block text-xs font-medium ${textSec}`}>
                        Empresa
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-1 transition-colors ${editMode ? inputClass : inputDisabled}`}
                        placeholder="Nombre de la empresa"
                      />
                    </div>

                    {user?.currentPlan?.slug === 'empresarial' && (
                      <div className="space-y-2">
                        <label className={`block text-xs font-medium ${textSec}`}>
                          Cargo
                        </label>
                        <div className={`w-full px-3 py-2.5 border rounded-lg text-sm ${inputDisabled} flex items-center`}>
                          <span className={textMuted}>{user?.position || 'Sin cargo asignado'}</span>
                        </div>
                        <p className={`text-xs ${textMuted}`}>El cargo es asignado por el administrador de la empresa</p>
                      </div>
                    )}

                    <div className="sm:col-span-2 space-y-2">
                      <label className={`block text-xs font-medium ${textSec}`}>
                        Dirección
                      </label>
                      <div className="relative">
                        <FiMapPin className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${textMuted}`} />
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-1 transition-colors ${editMode ? inputClass : inputDisabled}`}
                          placeholder="Tu dirección completa"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div className={`xl:col-span-2 ${box} rounded-2xl border overflow-hidden`}>
                <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${modoOscuro ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                      <FiAward className={`text-base ${modoOscuro ? 'text-gray-300' : 'text-gray-900'}`} />
                    </div>
                    <div>
                      <h2 className={`text-sm font-semibold ${textPri}`}>Información de la cuenta</h2>
                      <p className={`text-xs ${textMuted}`}>Rol, estado y actividad de acceso.</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-px sm:grid-cols-4">
                  {[
                    {
                      label: 'Rol',
                      value: user?.isSuperAdmin ? 'Super Administrador' : user?.role === 'admin' ? 'Administrador' : 'Usuario',
                    },
                    { label: 'Estado', value: 'Activo' },
                    { label: 'Miembro desde', value: formatAccountDate(user?.createdAt) },
                    { label: 'Última actividad', value: formatAccountDate(user?.lastLogin, true) },
                  ].map((item) => (
                    <div key={item.label} className={`min-w-0 p-4 ${modoOscuro ? 'bg-gray-800/40' : 'bg-gray-50/70'}`}>
                      <p className={`text-[11px] font-medium uppercase tracking-wide ${textMuted}`}>{item.label}</p>
                      <p className={`mt-1 truncate text-sm font-semibold ${textPri}`} title={item.value}>{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="xl:col-span-2">
                <ComprobanteBrandingPanel
                  user={user}
                  modoOscuro={modoOscuro}
                  textPri={textPri}
                  textSec={textSec}
                  textMuted={textMuted}
                  box={box}
                  inputClass={inputClass}
                />
              </div>

              <CuentaDesktopInstallPanel
                box={box}
                textPri={textPri}
                textMuted={textMuted}
                modoOscuro={modoOscuro}
              />

            </>
          )}

          {activeTab === 'security' && (
            <>
            <div className={`lg:col-span-2 ${box} rounded-2xl border overflow-hidden`}>
              <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${modoOscuro ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                    <FiShield className={`text-base ${modoOscuro ? 'text-gray-300' : 'text-gray-900'}`} />
                  </div>
                  <div>
                    <h2 className={`text-sm font-semibold ${textPri}`}>Seguridad</h2>
                    <p className={`text-xs ${textMuted}`}>Cambio de contraseña.</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handlePasswordUpdate} className="p-4 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className={`block text-xs font-medium ${textSec}`}>
                      Contraseña actual
                    </label>
                    <div className="relative">
                      <FiLock className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${textMuted}`} />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className={`w-full pl-9 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-1 ${inputClass}`}
                        placeholder="Tu contraseña actual"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${textMuted} hover:opacity-80`}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={`block text-xs font-medium ${textSec}`}>
                      Nueva contraseña
                    </label>
                    <div className="relative">
                      <FiLock className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${textMuted}`} />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className={`w-full pl-9 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-1 ${inputClass}`}
                        placeholder="Nueva contraseña"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${textMuted} hover:opacity-80`}
                      >
                        {showNewPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className={`block text-xs font-medium ${textSec}`}>
                      Confirmar nueva contraseña
                    </label>
                    <div className="relative">
                      <FiLock className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${textMuted}`} />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className={`w-full pl-9 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-1 ${inputClass}`}
                        placeholder="Confirma la nueva contraseña"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${textMuted} hover:opacity-80`}
                      >
                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full text-white py-2.5 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${modoOscuro ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-900 hover:bg-black'}`}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  ) : (
                    <FiCheck className="text-lg" />
                  )}
                  Cambiar contraseña
                </button>
              </form>
            </div>

            <div className={`lg:col-span-2 ${box} rounded-2xl border overflow-hidden`}>
              <div className={`flex items-center justify-between gap-3 px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${modoOscuro ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                    <FiShield className={twoFactorStatus.enabled ? 'text-emerald-500' : textMuted} />
                  </div>
                  <div>
                    <h2 className={`text-sm font-semibold ${textPri}`}>Autenticación en dos pasos</h2>
                    <p className={`text-xs ${textMuted}`}>Protegé tu cuenta con una app autenticadora.</p>
                  </div>
                </div>
                <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${
                  twoFactorStatus.enabled
                    ? modoOscuro ? 'border-emerald-800 bg-emerald-950/50 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : modoOscuro ? 'border-gray-600 text-gray-400' : 'border-gray-200 text-gray-500'
                }`}>
                  {twoFactorStatus.enabled ? 'Activada' : 'Desactivada'}
                </span>
              </div>

              <div className="p-4 sm:p-5">
                {twoFactorRecoveryCodes.length > 0 ? (
                  <div className={`rounded-xl border p-4 ${modoOscuro ? 'border-amber-800 bg-amber-950/20' : 'border-amber-200 bg-amber-50'}`}>
                    <h3 className={`text-sm font-semibold ${textPri}`}>Guardá tus códigos de recuperación</h3>
                    <p className={`mt-1 text-xs ${textMuted}`}>Se muestran una sola vez. Cada código permite ingresar sin la app.</p>
                    <div className={`mt-3 grid grid-cols-2 gap-2 rounded-lg p-3 font-mono text-sm ${modoOscuro ? 'bg-black/30 text-amber-200' : 'bg-white text-amber-900'}`}>
                      {twoFactorRecoveryCodes.map((code) => <span key={code}>{code}</span>)}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(twoFactorRecoveryCodes.join('\n'))}
                      className={`mt-3 rounded-lg border px-3 py-1.5 text-xs font-semibold ${btnOut}`}
                    >
                      Copiar códigos
                    </button>
                  </div>
                ) : twoFactorSetup ? (
                  <form onSubmit={enableTwoFactor} className="grid grid-cols-1 gap-5 md:grid-cols-[240px_1fr]">
                    <div className="rounded-xl bg-white p-3">
                      <img src={twoFactorSetup.qrCodeDataUrl} alt="Código QR para configurar 2FA" className="mx-auto h-auto w-full max-w-[216px]" />
                    </div>
                    <div>
                      <h3 className={`text-sm font-semibold ${textPri}`}>1. Escaneá el código QR</h3>
                      <p className={`mt-1 text-xs ${textMuted}`}>Usá Google Authenticator, Microsoft Authenticator, Authy u otra app compatible.</p>
                      <p className={`mt-3 text-xs ${textMuted}`}>Clave manual</p>
                      <code className={`mt-1 block break-all rounded-lg px-3 py-2 text-xs ${modoOscuro ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                        {twoFactorSetup.secret}
                      </code>
                      <label className={`mt-4 block text-xs font-medium ${textSec}`}>2. Ingresá el código de 6 dígitos</label>
                      <input
                        value={twoFactorCode}
                        onChange={(event) => setTwoFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="123456"
                        className={`mt-1 w-full max-w-xs rounded-lg border px-3 py-2.5 text-sm ${inputClass}`}
                        required
                      />
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="submit"
                          disabled={twoFactorLoading || twoFactorCode.length !== 6}
                          className="rounded-lg bg-[#1F80FF] px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
                        >
                          {twoFactorLoading ? 'Verificando…' : 'Activar 2FA'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTwoFactorSetup(null);
                            setTwoFactorCode('');
                          }}
                          className={`rounded-lg border px-4 py-2 text-xs font-semibold ${btnOut}`}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </form>
                ) : twoFactorStatus.enabled ? (
                  <form onSubmit={disableTwoFactor} className="max-w-xl">
                    <p className={`text-sm ${textSec}`}>
                      El inicio de sesión solicita un código adicional. Te quedan {twoFactorStatus.recoveryCodesRemaining} códigos de recuperación.
                      {' '}Al descargar Excel, PDF o pack del contador también te pediremos el código de la app.
                    </p>
                    <label className={`mt-4 block text-xs font-medium ${textSec}`}>Código de la app o de recuperación</label>
                    <input
                      value={twoFactorCode}
                      onChange={(event) => setTwoFactorCode(event.target.value)}
                      autoComplete="one-time-code"
                      placeholder="123456 o XXXXX-XXXXX"
                      className={`mt-1 w-full max-w-xs rounded-lg border px-3 py-2.5 text-sm ${inputClass}`}
                      required
                    />
                    <button
                      type="submit"
                      disabled={twoFactorLoading || !twoFactorCode.trim()}
                      className={`mt-3 rounded-lg border px-4 py-2 text-xs font-semibold disabled:opacity-50 ${
                        modoOscuro ? 'border-red-800 text-red-300 hover:bg-red-950/30' : 'border-red-200 text-red-700 hover:bg-red-50'
                      }`}
                    >
                      {twoFactorLoading ? 'Desactivando…' : 'Desactivar 2FA'}
                    </button>
                  </form>
                ) : (
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className={`text-sm ${textSec}`}>Al iniciar sesión, vas a confirmar tu identidad con un código temporal.</p>
                      <p className={`mt-1 text-xs ${textMuted}`}>No usamos SMS. También recibirás códigos de recuperación de un solo uso.</p>
                    </div>
                    <button
                      type="button"
                      onClick={startTwoFactorSetup}
                      disabled={twoFactorLoading}
                      className="shrink-0 rounded-lg bg-[#1F80FF] px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      {twoFactorLoading ? 'Preparando…' : 'Configurar 2FA'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {activeTab === 'fiscal' && (
            <div className={`${box} rounded-2xl border overflow-hidden xl:col-span-2`}>
              <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                <h2 className={`text-sm font-semibold ${textPri}`}>Régimen fiscal</h2>
                <p className={`text-xs ${textMuted}`}>Se usa en el estado de resultados para topes de Monotributo.</p>
              </div>
              <form onSubmit={handleFiscalSave} className="p-4 sm:p-5 space-y-4">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Régimen</label>
                  <select
                    value={fiscalForm.regimenFiscal}
                    onChange={(e) => setFiscalForm((p) => ({ ...p, regimenFiscal: e.target.value }))}
                    className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                  >
                    <option value="">Sin declarar</option>
                    <option value="monotributo">Monotributo</option>
                    <option value="ri">Responsable inscripto</option>
                  </select>
                </div>
                {fiscalForm.regimenFiscal === 'monotributo' && (
                  <>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Categoría</label>
                      <select
                        value={fiscalForm.categoriaMonotributo}
                        onChange={(e) => setFiscalForm((p) => ({ ...p, categoriaMonotributo: e.target.value }))}
                        className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                      >
                        <option value="">Elegí categoría</option>
                        {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'].map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Actividad</label>
                      <select
                        value={fiscalForm.actividad}
                        onChange={(e) => setFiscalForm((p) => ({ ...p, actividad: e.target.value }))}
                        className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                      >
                        <option value="servicios">Servicios / locaciones</option>
                        <option value="bienes">Venta de bienes</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Vencimiento habitual (día del mes)</label>
                      <select
                        value={fiscalForm.vencimientoMonotributoDia}
                        onChange={(e) => setFiscalForm((p) => ({
                          ...p,
                          vencimientoMonotributoDia: Number(e.target.value),
                        }))}
                        className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                      >
                        {[5, 10, 15, 20].map((d) => (
                          <option key={d} value={d}>Día {d} de cada mes</option>
                        ))}
                      </select>
                      <p className={`text-[11px] mt-1 ${textMuted}`}>
                        Orientativo según tu calendario de pagos; revisá en Mi monotributo.
                      </p>
                    </div>
                    <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                      <input
                        type="checkbox"
                        className="mt-0.5 rounded border-gray-300"
                        checked={fiscalForm.recordarCuotaMonotributo}
                        onChange={(e) => setFiscalForm((p) => ({
                          ...p,
                          recordarCuotaMonotributo: e.target.checked,
                        }))}
                      />
                      <span className="min-w-0">
                        <span className={`block text-sm font-semibold ${textPri}`}>
                          Recordar cuota mensual en egresos
                        </span>
                        <span className={`block text-xs mt-0.5 ${textMuted}`}>
                          Creamos o actualizamos una plantilla recurrente con el monto estimado de tu categoría (vencimiento orientativo día 10).
                        </span>
                      </span>
                    </label>
                  </>
                )}
                {fiscalDisclaimer && (
                  <p className={`text-xs ${textMuted}`}>{fiscalDisclaimer}</p>
                )}

                {multimoneda && (
                  <div className={`rounded-xl border p-4 space-y-3 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                    <div>
                      <p className={`text-sm font-semibold ${textPri}`}>Cotización USD → ARS</p>
                      <p className={`text-xs mt-0.5 ${textMuted}`}>
                        Se usa al registrar ventas o egresos en dólares. Por defecto: Blue.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'blue', label: 'Dólar Blue' },
                        { id: 'mep', label: 'Dólar MEP' },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          disabled={fxSaving}
                          onClick={() => handleFxPreferenceSave(opt.id)}
                          className={`px-3 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-50 ${
                            fxPreference === opt.id
                              ? 'bg-[#1F80FF] text-white border-[#1F80FF]'
                              : modoOscuro
                                ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                                : 'border-gray-200 text-gray-700 hover:bg-white'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
                >
                  <FiSave className="w-4 h-4" />
                  Guardar régimen
                </button>
              </form>

              {hasProfesional && (
                <div className={`p-4 sm:p-5 space-y-4 border-t ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <FiscalSimulator modoOscuro={modoOscuro} regimen={fiscalForm.regimenFiscal} />
                  <FiscalChecklist modoOscuro={modoOscuro} />
                </div>
              )}

              {fiscalCalendario.length > 0 && (
                <div className={`border-t ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`px-4 py-3 flex items-center gap-2`}>
                    <FiCalendar className="w-4 h-4 text-[#1F80FF]" />
                    <h3 className={`text-sm font-semibold ${textPri}`}>Próximos vencimientos fiscales</h3>
                  </div>
                  <ul className={`divide-y ${modoOscuro ? 'divide-gray-700' : 'divide-gray-100'}`}>
                    {fiscalCalendario.map((ev) => (
                      <li key={ev.id} className="px-4 py-3">
                        <div className={`text-sm font-medium ${textPri}`}>{ev.titulo}</div>
                        <div className={`text-xs mt-0.5 ${textMuted}`}>
                          {ev.fecha} · {ev.diasRestantes === 0 ? 'Hoy' : `En ${ev.diasRestantes} día(s)`}
                        </div>
                        {ev.descripcion && (
                          <div className={`text-xs mt-1 ${textMuted}`}>{ev.descripcion}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'cobro' && hasCobranzasEssential && (
            <CollectionPaymentSetupForm
              notificationForm={notificationForm}
              setNotificationForm={setNotificationForm}
              onSubmit={handleNotificationSave}
              saving={notificationSaving}
              secureFetch={secureFetch}
              hasProfesional={hasProfesional}
              modoOscuro={modoOscuro}
              inputClass={inputClass}
              textPri={textPri}
              textMuted={textMuted}
              box={box}
            />
          )}

          {activeTab === 'notifications' && hasProfesional && (
            <div className={`${box} rounded-2xl border overflow-hidden xl:col-span-2`}>
              <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                <h2 className={`text-sm font-semibold ${textPri}`}>Notificaciones por email</h2>
                <p className={`text-xs ${textMuted}`}>
                  Resúmenes y alertas por email. Los datos de cobro (alias, CBU, link) están en la pestaña{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/cuenta?tab=cobro')}
                    className="font-semibold text-[#1F80FF] hover:underline"
                  >
                    Cobrar
                  </button>
                  .
                </p>
              </div>
              <form onSubmit={handleNotificationSave} className="p-4 sm:p-5 space-y-4">
                <label className={`flex items-start gap-3 cursor-pointer rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={notificationForm.monthlyReportEnabled}
                    onChange={(e) => setNotificationForm((p) => ({ ...p, monthlyReportEnabled: e.target.checked }))}
                    className="mt-1 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
                  />
                  <span>
                    <span className={`block text-sm font-semibold ${textPri}`}>Recibir resumen mensual</span>
                    <span className={`block text-xs mt-0.5 ${textMuted}`}>
                      Flujo de caja del mes anterior, impagas y link a reportes. Se envía a {user?.email}.
                    </span>
                  </span>
                </label>

                {notificationForm.monthlyReportEnabled && (
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Día de envío mensual</label>
                    <select
                      value={notificationForm.monthlyReportDay}
                      onChange={(e) => setNotificationForm((p) => ({ ...p, monthlyReportDay: Number(e.target.value) }))}
                      className={`w-full max-w-xs rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                    >
                      <option value={1}>Día 1 de cada mes</option>
                      <option value={5}>Día 5 de cada mes</option>
                      <option value={10}>Día 10 de cada mes</option>
                    </select>
                  </div>
                )}

                <label className={`flex items-start gap-3 cursor-pointer rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={notificationForm.weeklyReportEnabled}
                    onChange={(e) => setNotificationForm((p) => ({ ...p, weeklyReportEnabled: e.target.checked }))}
                    className="mt-1 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
                  />
                  <span>
                    <span className={`block text-sm font-semibold ${textPri}`}>Recibir resumen semanal</span>
                    <span className={`block text-xs mt-0.5 ${textMuted}`}>
                      Flujo de los últimos 7 días, saldos pendientes y link al inicio. Se envía los lunes.
                    </span>
                  </span>
                </label>

                <label className={`flex items-start gap-3 cursor-pointer rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={notificationForm.dailyDigestEnabled}
                    onChange={(e) => setNotificationForm((p) => ({ ...p, dailyDigestEnabled: e.target.checked }))}
                    className="mt-1 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
                  />
                  <span>
                    <span className={`block text-sm font-semibold ${textPri}`}>Recibir resumen diario de cobranzas</span>
                    <span className={`block text-xs mt-0.5 ${textMuted}`}>
                      Cada mañana, las 3 impagas a priorizar hoy. Mismas cifras que Inicio. Orientativo; consultá a tu contador.
                    </span>
                  </span>
                </label>

                <div className={`rounded-xl border p-4 ${modoOscuro ? 'border-amber-800/60 bg-amber-950/20' : 'border-amber-200 bg-amber-50/60'}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationForm.fiscalLimitAlertEnabled}
                      onChange={(e) => setNotificationForm((p) => ({ ...p, fiscalLimitAlertEnabled: e.target.checked }))}
                      className="mt-1 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
                    />
                    <span className="flex-1">
                      <span className="flex items-center gap-2">
                        <FiAlertTriangle className={`h-4 w-4 ${modoOscuro ? 'text-amber-300' : 'text-amber-600'}`} />
                        <span className={`block text-sm font-semibold ${textPri}`}>Alerta por tope de Monotributo</span>
                      </span>
                      <span className={`block text-xs mt-1 ${textMuted}`}>
                        Te avisamos una vez por email cuando tu facturación de los últimos 12 meses alcance el 80% del tope de tu categoría.
                      </span>
                      {fiscalForm.regimenFiscal === 'monotributo' && fiscalForm.categoriaMonotributo ? (
                        <span className={`mt-1 block text-xs font-medium ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`}>
                          Categoría configurada: {fiscalForm.categoriaMonotributo}
                        </span>
                      ) : (
                        <span className={`mt-1 block text-xs font-medium ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`}>
                          Primero configurá Monotributo y tu categoría en la pestaña Fiscal.
                        </span>
                      )}
                    </span>
                  </label>
                  {notificationForm.fiscalLimitAlertEnabled && (
                    <div className={`mt-3 border-t pt-3 pl-7 ${modoOscuro ? 'border-amber-800/50' : 'border-amber-200'}`}>
                      <button
                        type="button"
                        onClick={handleTestFiscalAlert}
                        disabled={fiscalAlertTesting}
                        className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-xs font-semibold disabled:opacity-50 ${
                          modoOscuro
                            ? 'border-amber-700 text-amber-200 hover:bg-amber-900/30'
                            : 'border-amber-300 text-amber-800 hover:bg-amber-100'
                        }`}
                      >
                        <FiMail className="h-3.5 w-3.5" />
                        {fiscalAlertTesting ? 'Enviando…' : 'Probar alerta fiscal'}
                      </button>
                    </div>
                  )}
                </div>

                <label className={`flex items-start gap-3 cursor-pointer rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                  <input
                    type="checkbox"
                    checked={notificationForm.promiseAlertsEnabled}
                    onChange={(e) => setNotificationForm((p) => ({ ...p, promiseAlertsEnabled: e.target.checked }))}
                    className="mt-1 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
                  />
                  <span>
                    <span className={`block text-sm font-semibold ${textPri}`}>Alertas de promesas vencidas</span>
                    <span className={`block text-xs mt-0.5 ${textMuted}`}>
                      Si un cliente prometió pagar y no lo hizo, agregamos una nota automática y te avisamos por email.
                    </span>
                  </span>
                </label>

                <div className={`rounded-2xl border overflow-hidden ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`px-4 py-3 border-b flex items-center gap-2 ${modoOscuro ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50'}`}>
                    <span className={`p-1.5 rounded-lg ${modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'}`}>
                      <FiActivity className="w-4 h-4" />
                    </span>
                    <div>
                      <span className={`block text-sm font-semibold ${textPri}`}>Alertas de cobranza</span>
                      <span className={`block text-[11px] ${textMuted}`}>Email semanal · plan Profesional+</span>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationForm.collectionAlertsEnabled}
                        onChange={(e) => setNotificationForm((p) => ({ ...p, collectionAlertsEnabled: e.target.checked }))}
                        className="mt-1 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
                      />
                      <span>
                        <span className={`block text-sm font-medium ${textPri}`}>Activar alertas automáticas</span>
                        <span className={`block text-xs mt-0.5 ${textMuted}`}>
                          Te avisamos por email si el DSO empeora respecto al mes anterior, supera el umbral o la tasa de cobro cae por debajo del mínimo (máx. 1 por semana).
                        </span>
                      </span>
                    </label>
                    {notificationForm.collectionAlertsEnabled && (
                      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 pl-7 border-t ${modoOscuro ? 'border-gray-700' : 'border-gray-100'}`}>
                        <div>
                          <label className={`block text-xs font-semibold mb-1.5 ${textMuted}`}>DSO máximo (días)</label>
                          <input
                            type="number"
                            min={7}
                            max={180}
                            value={notificationForm.collectionDsoThreshold}
                            onChange={(e) => setNotificationForm((p) => ({ ...p, collectionDsoThreshold: Number(e.target.value) }))}
                            className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                          />
                        </div>
                        <div>
                          <label className={`block text-xs font-semibold mb-1.5 ${textMuted}`}>Tasa mínima de cobro (%)</label>
                          <input
                            type="number"
                            min={10}
                            max={100}
                            value={notificationForm.collectionMinRate}
                            onChange={(e) => setNotificationForm((p) => ({ ...p, collectionMinRate: Number(e.target.value) }))}
                            className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <button
                            type="button"
                            onClick={handleTestCollectionAlert}
                            disabled={collectionAlertTesting}
                            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold border disabled:opacity-50 ${modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`}
                          >
                            <FiMail className="w-3.5 h-3.5" />
                            {collectionAlertTesting ? 'Enviando…' : 'Probar alerta de cobranza'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`rounded-xl border p-4 space-y-3 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationForm.contadorPackEnabled}
                      onChange={(e) => setNotificationForm((p) => ({ ...p, contadorPackEnabled: e.target.checked }))}
                      className="mt-1 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
                    />
                    <span>
                      <span className={`block text-sm font-semibold ${textPri}`}>Envío automático del pack al contador</span>
                      <span className={`block text-xs mt-0.5 ${textMuted}`}>
                        ZIP mensual para tu contador. Elegí qué archivos incluir. Te copiamos en el email.
                      </span>
                    </span>
                  </label>

                  {notificationForm.contadorPackEnabled && (
                    <div className="space-y-3 pl-7">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Email del contador</label>
                          <input
                            type="email"
                            value={notificationForm.contadorEmail}
                            onChange={(e) => setNotificationForm((p) => ({ ...p, contadorEmail: e.target.value }))}
                            className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                            placeholder="contador@estudio.com"
                          />
                        </div>
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Día de envío mensual</label>
                          <select
                            value={notificationForm.contadorPackDay}
                            onChange={(e) => setNotificationForm((p) => ({ ...p, contadorPackDay: Number(e.target.value) }))}
                            className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                          >
                            <option value={1}>Día 1 de cada mes</option>
                            <option value={5}>Día 5 de cada mes</option>
                            <option value={10}>Día 10 de cada mes</option>
                          </select>
                        </div>
                      </div>
                      {notificationForm.contadorPackSchedule?.active && notificationForm.contadorEmail && (
                        <p className={`text-xs ${textMuted}`}>
                          Próximo envío programado: {notificationForm.contadorPackSchedule.nextRunLabel}
                          {notificationForm.contadorPackSchedule.periodLabel
                            ? ` · período ${notificationForm.contadorPackSchedule.periodLabel}`
                            : ''}
                          {notificationForm.contadorPackSchedule.lastSent
                            ? ` · último envío ${notificationForm.contadorPackSchedule.lastSent}`
                            : ''}
                        </p>
                      )}
                      <div>
                        <p className={`text-xs font-semibold mb-2 ${textMuted}`}>Incluir en el ZIP</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {CONTADOR_PACK_SECTION_OPTIONS.map((section) => (
                            <label key={section.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Boolean(notificationForm.contadorPackSections?.[section.id])}
                                onChange={(e) => setNotificationForm((p) => ({
                                  ...p,
                                  contadorPackSections: {
                                    ...p.contadorPackSections,
                                    [section.id]: e.target.checked,
                                  },
                                }))}
                                className="rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
                              />
                              <span className={`text-sm ${textPri}`}>{section.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`rounded-xl border p-4 space-y-3 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-start gap-2">
                    <FiUserPlus className={`w-4 h-4 mt-0.5 shrink-0 ${modoOscuro ? 'text-gray-300' : 'text-gray-700'}`} />
                    <div className="flex-1 space-y-3">
                      <div>
                        <span className={`block text-sm font-semibold ${textPri}`}>Invitar contador (Lector)</span>
                        <span className={`block text-xs mt-0.5 ${textMuted}`}>
                          Acceso solo lectura a ventas, egresos, impagas y pack contable. 1 contador en Profesional; más en Empresarial.
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Nombre</label>
                          <input
                            type="text"
                            value={contadorInviteForm.name}
                            onChange={(e) => setContadorInviteForm((p) => ({ ...p, name: e.target.value }))}
                            className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                            placeholder="María Contadora"
                          />
                        </div>
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Email</label>
                          <input
                            type="email"
                            value={contadorInviteForm.email}
                            onChange={(e) => setContadorInviteForm((p) => ({ ...p, email: e.target.value }))}
                            className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                            placeholder="contador@estudio.com"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <button
                            type="button"
                            onClick={handleContadorInvite}
                            disabled={contadorInviteLoading}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
                          >
                            <FiUserPlus className="w-4 h-4" />
                            {contadorInviteLoading ? 'Enviando…' : 'Enviar invitación'}
                          </button>
                        </div>
                      </div>
                      {contadorInvites.length > 0 && (
                        <div>
                          <p className={`text-xs font-medium mb-2 ${textMuted}`}>Invitaciones pendientes</p>
                          <ul className={`divide-y rounded-xl border ${modoOscuro ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-100'}`}>
                            {contadorInvites.map((inv) => (
                              <li key={inv.token || inv.id} className="px-3 py-2 text-sm flex justify-between gap-2">
                                <span className={textPri}>{inv.name || inv.email}</span>
                                <span className={`text-xs ${textMuted}`}>{inv.email}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className={`rounded-xl border p-3 space-y-2 ${modoOscuro ? 'border-gray-600 bg-gray-900/50' : 'border-gray-200 bg-white'}`}>
                        <p className={`text-xs font-semibold ${textPri}`}>Link solo lectura (sin cuenta)</p>
                        <p className={`text-xs ${textMuted}`}>
                          Tu contador ve resumen y descarga el pack ZIP sin registrarse. Válido 90 días.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={contadorGuestLinkLoading}
                            onClick={() => handleContadorGuestLink(false)}
                            className="rounded-lg bg-[#1F80FF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#004AAD] disabled:opacity-50"
                          >
                            {contadorGuestLinkLoading ? 'Generando…' : contadorGuestLink ? 'Ver link activo' : 'Generar link'}
                          </button>
                          {contadorGuestLink && (
                            <button
                              type="button"
                              onClick={copyContadorGuestLink}
                              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${btnOut}`}
                            >
                              Copiar link
                            </button>
                          )}
                          {contadorGuestLink && (
                            <button
                              type="button"
                              disabled={contadorGuestLinkLoading}
                              onClick={() => handleContadorGuestLink(true)}
                              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${btnOut}`}
                            >
                              Renovar link
                            </button>
                          )}
                        </div>
                        {contadorGuestLink && (
                          <p className={`text-[10px] break-all ${textMuted}`}>{contadorGuestLink}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={notificationSaving}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
                  >
                    <FiSave className="w-4 h-4" />
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={handleTestMonthlyReport}
                    disabled={notificationTesting}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border disabled:opacity-50 ${modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <FiMail className="w-4 h-4" />
                    {notificationTesting ? 'Enviando…' : 'Probar mensual'}
                  </button>
                  <button
                    type="button"
                    onClick={handleTestWeeklyReport}
                    disabled={weeklyTesting}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border disabled:opacity-50 ${modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <FiMail className="w-4 h-4" />
                    {weeklyTesting ? 'Enviando…' : 'Probar semanal'}
                  </button>
                  <button
                    type="button"
                    onClick={handleTestDailyDigest}
                    disabled={dailyDigestTesting}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border disabled:opacity-50 ${modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <FiMail className="w-4 h-4" />
                    {dailyDigestTesting ? 'Enviando…' : 'Probar diario'}
                  </button>
                  <button
                    type="button"
                    onClick={handleTestContadorPack}
                    disabled={contadorPackTesting}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border disabled:opacity-50 ${modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <FiMail className="w-4 h-4" />
                    {contadorPackTesting ? 'Enviando…' : 'Probar pack contador'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'libro' && isLedgerAccount && (
            <div className="xl:col-span-2">
              <PlatformLedgerPanel modoOscuro={modoOscuro} variant="cuenta" />
            </div>
          )}

          {activeTab === 'referidos' && (
            <div className="xl:col-span-2">
              <ReferralPanel
                modoOscuro={modoOscuro}
                box={box}
                textPri={textPri}
                textSec={textSec}
                textMuted={textMuted}
              />
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="xl:col-span-2 space-y-2">
              <div className={`${box} rounded-2xl border overflow-hidden`}>
                <div className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${modoOscuro ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                      <FiCreditCard className={`text-base ${modoOscuro ? 'text-gray-300' : 'text-gray-900'}`} />
                    </div>
                    <div>
                      <h2 className={`text-sm font-semibold ${textPri}`}>Suscripción y facturación</h2>
                      <p className={`text-xs ${textMuted}`}>Tu plan, renovación y pagos de Contsuite.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/planes')}
                    className="rounded-lg bg-[#1F80FF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#004AAD]"
                  >
                    Ver planes
                  </button>
                </div>

                {subscriptionLoading ? (
                  <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4 animate-pulse">
                    {[1, 2, 3, 4].map((item) => (
                      <div key={item} className={`h-20 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} />
                    ))}
                  </div>
                ) : subscriptionError ? (
                  <div className="p-4">
                    <FormAlert type="error">{subscriptionError}</FormAlert>
                  </div>
                ) : (
                  <>
                    {betaAccountInfo && (
                      <div
                        className={`mx-4 mt-4 rounded-xl border p-4 ${
                          betaAccountInfo.phase === 'ending_soon' || betaAccountInfo.phase === 'grace_after_beta'
                            ? modoOscuro
                              ? 'border-amber-700/50 bg-amber-950/30'
                              : 'border-amber-200 bg-amber-50'
                            : modoOscuro
                              ? 'border-[#1F80FF]/40 bg-[#1F80FF]/10'
                              : 'border-[#1F80FF]/25 bg-[#1F80FF]/5'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex gap-3 min-w-0">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-[#1F80FF]/30 text-white' : 'bg-[#1F80FF] text-white'}`}>
                              <FiStar className="w-5 h-5" />
                            </div>
                            <div>
                              <p className={`text-sm font-semibold ${textPri}`}>
                                {betaAccountInfo.phase === 'grace_after_beta'
                                  ? 'Finalizó tu período beta'
                                  : betaAccountInfo.phase === 'ended'
                                    ? 'Activá tu suscripción'
                                    : 'Beneficio beta Contsuite'}
                              </p>
                              <p className={`text-xs mt-1 ${textSec}`}>
                                {betaAccountInfo.phase === 'active' || betaAccountInfo.phase === 'ending_soon'
                                  ? `Plan Profesional gratis hasta el ${betaAccountInfo.endsLabel}. `
                                  : ''}
                                {betaAccountInfo.daysRemaining != null && betaAccountInfo.daysRemaining >= 0
                                  ? `Quedan ${betaAccountInfo.daysRemaining} día(s). `
                                  : betaAccountInfo.daysRemaining != null
                                    ? 'El beneficio gratuito ya venció. '
                                    : ''}
                                {betaAccountInfo.phase === 'grace_after_beta' && betaAccountInfo.graceEndsLabel
                                  ? `Solo lectura hasta el ${betaAccountInfo.graceEndsLabel}. `
                                  : ''}
                                Después de la beta, la suscripción paga se activa desde Planes con Mercado Pago.
                              </p>
                            </div>
                          </div>
                          {betaAccountInfo.showActivateCta && (
                            <button
                              type="button"
                              onClick={() => navigate('/planes')}
                              className="shrink-0 rounded-lg bg-[#1F80FF] px-3 py-2 text-xs font-semibold text-white hover:bg-[#004AAD]"
                            >
                              Activar suscripción
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
                      <div className={`rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`text-[11px] font-medium uppercase tracking-wide ${textMuted}`}>Plan actual</p>
                        <p className={`mt-1 text-base font-bold ${textPri}`}>
                          {subscriptionSummary?.plan?.name || (user?.isSuperAdmin ? 'Acceso administrativo' : 'Sin plan')}
                        </p>
                      </div>
                      <div className={`rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`text-[11px] font-medium uppercase tracking-wide ${textMuted}`}>Ciclo</p>
                        <p className={`mt-1 text-base font-bold ${textPri}`}>
                          {subscriptionSummary?.subscription?.billingCycle === 'annual' ? 'Anual' : 'Mensual'}
                        </p>
                        {subscriptionSummary?.plan && (
                          <p className={`mt-0.5 text-xs ${textMuted}`}>
                            {formatBillingAmount(
                              subscriptionSummary.subscription?.billingCycle === 'annual'
                                ? (() => {
                                    const monthly = Number(subscriptionSummary.plan.price || 0);
                                    const annual = Number(subscriptionSummary.plan.annualPrice || 0);
                                    return annual >= monthly ? annual : monthly * 10;
                                  })()
                                : Number(subscriptionSummary.plan.price || subscriptionSummary.plan.monthlyPrice || 0),
                              'ARS'
                            )}
                            /{subscriptionSummary.subscription?.billingCycle === 'annual' ? 'año' : 'mes'}
                          </p>
                        )}
                      </div>
                      <div className={`rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`text-[11px] font-medium uppercase tracking-wide ${textMuted}`}>Estado</p>
                        <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                          ['Activa', 'Período de prueba'].includes(subscriptionStatus)
                            ? modoOscuro ? 'border-emerald-800 bg-emerald-950/50 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : modoOscuro ? 'border-gray-600 bg-gray-800 text-gray-300' : 'border-gray-200 bg-white text-gray-700'
                        }`}>
                          {subscriptionStatus}
                        </span>
                      </div>
                      <div className={`rounded-xl border p-4 ${modoOscuro ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`text-[11px] font-medium uppercase tracking-wide ${textMuted}`}>
                          {betaAccountInfo
                            ? 'Fin beneficio beta'
                            : subscriptionSummary?.subscription?.status === 'trialing'
                              ? 'Fin de prueba'
                              : 'Próxima renovación'}
                        </p>
                        <p className={`mt-1 text-sm font-semibold ${textPri}`}>
                          {formatAccountDate(
                            betaAccountInfo?.endsAt
                              || (subscriptionSummary?.subscription?.status === 'trialing'
                                ? subscriptionSummary?.subscription?.trialEndsAt
                                : subscriptionSummary?.subscription?.endDate)
                          )}
                        </p>
                      </div>
                    </div>

                    <div className={`flex flex-wrap items-center gap-2 border-t px-4 py-3 ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                      {betaAccountInfo?.showActivateCta && betaAccountInfo.phase !== 'active' && (
                        <button type="button" onClick={() => navigate('/planes')} className="rounded-lg bg-[#1F80FF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#004AAD]">
                          Activar suscripción
                        </button>
                      )}
                      {!betaAccountInfo && mustRegularizeSubscription(user) && (
                      <button type="button" onClick={() => navigate('/planes?pago=pendiente')} className="rounded-lg bg-[#1F80FF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#004AAD]">
                        Regularizar pago
                      </button>
                      )}
                      {['grace_readonly', 'past_due', 'suspended'].includes(
                        String(subscriptionSummary?.subscription?.status || '').toLowerCase()
                      ) && subscriptionSummary?.subscription?.graceEndsAt && (
                        <p className={`w-full text-xs ${textSec}`}>
                          Gracia de solo lectura hasta{' '}
                          {formatAccountDate(subscriptionSummary.subscription.graceEndsAt)}.
                        </p>
                      )}
                      {String(subscriptionSummary?.subscription?.status || '').toLowerCase() === 'suspended'
                        && subscriptionSummary?.subscription?.dataRetentionUntil && (
                        <p className={`w-full text-xs ${textSec}`}>
                          Retención de datos hasta{' '}
                          {formatAccountDate(subscriptionSummary.subscription.dataRetentionUntil)}.
                          Reactivá el plan para recuperar el acceso completo.
                        </p>
                      )}
                      <button type="button" onClick={() => navigate('/uso-plan')} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${btnOut}`}>
                        Ver uso del plan
                      </button>
                      {subscriptionSummary?.subscription?.paymentProvider === 'mercadopago' && (
                        <a
                          href="https://www.mercadopago.com.ar/activities"
                          target="_blank"
                          rel="noreferrer"
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${btnOut}`}
                        >
                          Abrir Mercado Pago
                          <FiExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      {subscriptionSummary?.subscription?.cancelAtPeriodEnd && (
                        <p className={`w-full text-xs ${textSec}`}>
                          Cancelaste la renovación automática. Podés usar el plan hasta{' '}
                          {formatAccountDate(
                            subscriptionSummary.subscription.accessUntil
                              || subscriptionSummary.subscription.endDate
                              || subscriptionSummary.subscription.trialEndsAt
                          )}
                          . Después pasás a plan cancelado hasta que vuelvas a suscribirte.
                        </p>
                      )}
                      {user?.role === 'employee' && !subscriptionSummary?.policy?.canManageSubscription && (
                        <p className={`w-full text-xs ${textMuted}`}>
                          La suscripción la administra el titular de la empresa desde su cuenta de Contsuite.
                        </p>
                      )}
                      {canCancelSubscription && (
                        <button
                          type="button"
                          onClick={() => setShowCancelSubscription(true)}
                          className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                            modoOscuro
                              ? 'border-red-900/60 text-red-300 hover:bg-red-950/40'
                              : 'border-red-200 text-red-700 hover:bg-red-50'
                          }`}
                        >
                          Cancelar suscripción
                        </button>
                      )}
                      <span className={`text-xs sm:ml-auto ${textMuted}`}>
                        {subscriptionSummary?.subscription?.cancelAtPeriodEnd
                          ? 'Renovación automática desactivada'
                          : subscriptionSummary?.subscription?.autoRenew
                            ? 'Renovación automática activa'
                            : 'Renovación automática no configurada'}
                      </span>
                    </div>

                    {showCancelSubscription && (
                      <div className={`border-t px-4 py-4 ${modoOscuro ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                        <p className={`text-sm font-semibold ${textPri}`}>¿Cancelar la renovación?</p>
                        <p className={`mt-1 text-xs ${textSec}`}>
                          Dejás de renovar en Mercado Pago, pero mantenés el acceso hasta la fecha de{' '}
                          {subscriptionSummary?.subscription?.status === 'trialing' ? 'fin de prueba' : 'próxima renovación'}{' '}
                          (
                          {formatAccountDate(
                            subscriptionSummary?.subscription?.status === 'trialing'
                              ? subscriptionSummary?.subscription?.trialEndsAt
                              : subscriptionSummary?.subscription?.endDate
                          )}
                          ). No hay reembolso del período en curso.
                        </p>
                        <ul className={`mt-2 list-disc pl-4 space-y-1 text-[11px] ${textMuted}`}>
                          {subscriptionPolicyBullets.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={subscriptionCancelLoading}
                            onClick={handleCancelSubscription}
                            className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                          >
                            {subscriptionCancelLoading ? 'Cancelando…' : 'Confirmar cancelación'}
                          </button>
                          <button
                            type="button"
                            disabled={subscriptionCancelLoading}
                            onClick={() => setShowCancelSubscription(false)}
                            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${btnOut}`}
                          >
                            Volver
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className={`${box} rounded-2xl border overflow-hidden`}>
                <div className={`flex items-center gap-2.5 border-b px-4 py-3 ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
                  <FiFileText className={`h-4 w-4 ${textMuted}`} />
                  <div>
                    <h2 className={`text-sm font-semibold ${textPri}`}>Pagos y comprobantes</h2>
                    <p className={`text-xs ${textMuted}`}>Operaciones de suscripción registradas en Contsuite.</p>
                  </div>
                </div>
                {subscriptionLoading ? (
                  <div className={`p-6 text-center text-sm ${textMuted}`}>Cargando pagos…</div>
                ) : subscriptionSummary?.payments?.length ? (
                  <div className={modoOscuro ? 'divide-y divide-gray-700' : 'divide-y divide-gray-100'}>
                    {subscriptionSummary.payments.map((payment) => (
                      <div key={payment.id} className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 sm:grid-cols-[1fr_140px_120px_110px]">
                        <div className="min-w-0">
                          <p className={`truncate text-sm font-semibold ${textPri}`}>{payment.planName}</p>
                          <p className={`text-xs ${textMuted}`}>{formatAccountDate(payment.createdAt)}</p>
                          <p className={`text-xs mt-0.5 sm:hidden ${
                            ['approved', 'authorized', 'active'].includes(String(payment.status).toLowerCase())
                              ? modoOscuro ? 'text-emerald-400' : 'text-emerald-700'
                              : textMuted
                          }`}>
                            {paymentStatus(payment.status)}
                          </p>
                        </div>
                        <p className={`hidden text-sm sm:block ${textSec}`}>
                          {payment.billingCycle === 'annual' ? 'Anual' : 'Mensual'}
                        </p>
                        <p className={`text-right text-sm font-semibold sm:text-left ${textPri}`}>
                          {formatBillingAmount(payment.amount, payment.currency)}
                        </p>
                        <span className={`hidden justify-self-start rounded-full border px-2 py-0.5 text-xs font-medium sm:inline-flex ${
                          ['approved', 'authorized', 'active'].includes(String(payment.status).toLowerCase())
                            ? modoOscuro ? 'border-emerald-800 text-emerald-300' : 'border-emerald-200 text-emerald-700'
                            : modoOscuro ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'
                        }`}>
                          {paymentStatus(payment.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <p className={`text-sm font-medium ${textPri}`}>Todavía no hay pagos registrados</p>
                    <p className={`mt-1 text-xs ${textMuted}`}>Cuando contrates o cambies un plan, aparecerán acá.</p>
                  </div>
                )}
                <div className={`border-t px-4 py-3 text-xs space-y-1 ${modoOscuro ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                  <p>
                    Los comprobantes fiscales del cobro se consultan y descargan desde Mercado Pago.
                  </p>
                  <p>
                    En Mercado Pago la <strong className="font-medium">liquidación</strong> del dinero en tu cuenta puede demorar
                    unos días; no es lo mismo que un pago rechazado. Si tu plan figura activo acá, el cobro de la suscripción
                    está confirmado.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cuenta; 